<template>
  <sdf-alert-banner
    v-show="show"
    :aria-live="ariaLive"
    v-bind="webComponentProps"
    :class="attrs.class"
    @sdfDismiss="$emit('sdf-dismiss', $event)"
    @sdfAfterClose="$emit('sdf-after-close', $event)"
    @sdfAfterOpen="$emit('sdf-after-open', $event)"
  >
    <template v-for="slotName in sdfAlertBannerSlotNames" :key="slotName">
      <div v-if="$slots[slotName]" :slot="slotName !== 'default' ? slotName : null" class="contents">
        <slot :name="slotName"></slot>
      </div>
    </template>
  </sdf-alert-banner>
</template>

<script setup lang="ts">
import type { JSX, SdfAlertBannerCustomEvent } from '@waypoint/web-components';
import { useAttrs } from 'vue';
import { useWebComponentProps } from '@/composables/useWebComponentProps';
import { sdfAlertBannerSlotNames, type SdfAlertBannerSlots } from '@/waypoint-vue-wrappers/types/slots';

defineOptions({
  name: 'SdfAlertBanner',
  inheritAttrs: false,
});

type Props = Partial<JSX.SdfAlertBanner> & {
  ariaLive?: 'assertive' | 'polite' | 'off';
  show?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  ariaLive: 'assertive',
  show: true,
});

defineSlots<SdfAlertBannerSlots>();

defineEmits<{
  'sdf-dismiss': [event: SdfAlertBannerCustomEvent<void>];
  'sdf-after-close': [event: SdfAlertBannerCustomEvent<void>];
  'sdf-after-open': [event: SdfAlertBannerCustomEvent<void>];
}>();

const webComponentProps = useWebComponentProps(props, { exclude: ['ariaLive', 'show'] });

const attrs = useAttrs();
</script>
